import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Clock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { CATS } from "./InternshipLayout";

const PAGE = 12;

function Card({ d }) {
  return (
    <div className="group bg-white border border-border rounded-2xl overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300" data-testid={`domain-card-${d.slug}`}>
      <div className="aspect-[16/9] bg-muted overflow-hidden"><img src={d.image} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
      <div className="p-5 flex flex-col flex-1">
        <div className="text-[11px] font-bold uppercase tracking-wider text-amber">{CATS.find((c) => c[0] === d.category)?.[1]}</div>
        <h3 className="font-heading font-bold text-navy text-lg mt-1">{d.title}</h3>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{d.short}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">{d.skills.slice(0, 3).map((s) => <span key={s} className="text-[11px] bg-muted rounded-full px-2.5 py-1">{s}</span>)}</div>
        <p className="text-xs text-muted-foreground mt-3">For: {d.eligible.slice(0, 3).join(", ")}</p>
        <div className="mt-auto pt-4 flex items-center justify-between">
          <span className="text-xs font-semibold inline-flex items-center gap-1 text-navy"><Clock size={13} /> 3 Months</span>
          <div className="flex gap-2">
            <Link to={`/internship/${d.slug}`} className="text-sm font-semibold px-3 py-1.5 rounded-full border border-border hover:border-navy transition-colors" data-testid={`domain-view-${d.slug}`}>View Details</Link>
            <Link to={`/internship/apply?domain=${d.slug}`} className="text-sm font-semibold px-3 py-1.5 rounded-full viriks-amber text-white" data-testid={`domain-apply-${d.slug}`}>Apply</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DomainExplorer() {
  const [params] = useSearchParams();
  const [domains, setDomains] = useState([]);
  const [cat, setCat] = useState(params.get("category") || "all");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => { api.get("/internship/domains").then(({ data }) => setDomains(data)); }, []);
  useEffect(() => { setCat(params.get("category") || "all"); }, [params]);
  useEffect(() => { setLimit(PAGE); }, [cat, q]);
  const list = useMemo(() => domains.filter((d) => (cat === "all" || d.category === cat) && `${d.title} ${d.short} ${d.skills.join(" ")}`.toLowerCase().includes(q.toLowerCase())), [domains, cat, q]);

  return (
    <section id="explore" className="max-w-7xl mx-auto px-6 py-24 scroll-mt-16" data-testid="intern-explore">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div><div className="text-xs font-bold tracking-[0.2em] uppercase text-amber mb-3">Explore internships</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">{domains.length || "50"} professional domains. Pick yours.</h2></div>
        <div className="relative w-full sm:w-80"><Search size={16} className="absolute left-3 top-3 text-muted-foreground" /><Input className="pl-9 h-11 rounded-full" placeholder="Search domain or skill" value={q} onChange={(e) => setQ(e.target.value)} data-testid="domain-search" /></div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-8">
        {CATS.map(([k, l]) => <button key={k} onClick={() => setCat(k)} className={`shrink-0 text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${cat === k ? "bg-[#111111] text-white border-[#111111]" : "border-border hover:border-navy"}`} data-testid={`domain-filter-${k}`}>{l}</button>)}
      </div>
      {list.length === 0 ? <p className="text-muted-foreground" data-testid="domain-empty">No internships match your search.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">{list.slice(0, limit).map((d) => <Card key={d.slug} d={d} />)}</div>
      )}
      {list.length > limit && (
        <div className="text-center mt-10"><button onClick={() => setLimit(limit + PAGE)} className="rounded-full border border-navy px-6 py-3 font-semibold inline-flex items-center gap-2 hover:bg-[#111111] hover:text-white transition-colors" data-testid="domain-load-more">Load more ({list.length - limit} more) <ArrowRight size={15} /></button></div>
      )}
    </section>
  );
}
